import type {
  ReportCatalogue,
  ReportDescriptor,
  ReportParamDescriptor,
  ReportPreview,
} from '@fruuit/shared'
import {
  Button,
  buttonClasses,
  Callout,
  Card,
  type Column,
  cn,
  DataTable,
  DateInput,
  Input,
  LoadingRegion,
  PageHeader,
  Select,
  useToast,
} from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Download, ExternalLink, FileText, Printer } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { API_BASE, buildUrl, errorMessage } from '../../api/client'
import { useApiQuery } from '../../api/hooks'
import { useTitle } from '../../lib/useTitle'
import { downloadExport } from '../exports/download'
import { ExportHistory, exportLogKey } from '../exports/ExportHistory'

type Area = 'app' | 'workspace'
type Params = Record<string, string>

/**
 * The committee report opens in another tab and is logged there, so the export history on
 * this page polls once after the server has had time to write the log entry, and once more
 * for a slow render (UX audit P2-8).
 */
export const REPORT_LOG_REFRESH_MS = [2500, 8000] as const

const COMMITTEE_CONTENTS = [
  'Executive overview',
  'Engagement narratives: the released history of each case and its next step',
  'Consolidated portfolio: coverage, flagged companies, progress by stage, client actions',
  'Material developments in the period',
  'Upcoming meetings',
  'Basis of preparation',
]

function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

function cellText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

/** Only the parameters this report takes, with empty values dropped. */
function paramsFor(report: ReportDescriptor | undefined, params: Params): Params {
  if (!report) return {}
  const out: Params = {}
  for (const p of report.params) {
    const v = params[p.key]
    if (v) out[p.key] = v
  }
  return out
}

/**
 * S9 Reports (X-05): pick a report, set its scope, preview the rows the export
 * will contain (P-RPT-001), download the CSV or open the print-ready committee
 * report, and see the export history.
 */
export function ReportsView({ area }: { area: Area }) {
  useTitle('Reports')
  const view = area === 'app' ? 'investor' : 'staff'
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState('committee')
  const [params, setParams] = useState<Params>({})
  const [downloading, setDownloading] = useState(false)
  const org = params.org ?? ''

  const catalogueQuery = useApiQuery<ReportCatalogue>(
    ['reports', 'catalogue', view, org],
    '/reports',
    {
      query: { view, org },
    },
  )
  const catalogue = catalogueQuery.data
  useEffect(() => {
    if (!catalogue) return
    setParams((p) => ({ from: catalogue.defaults.from, to: catalogue.defaults.to, ...p }))
  }, [catalogue])

  const report = catalogue?.reports.find((r) => r.id === selected)
  const scoped = useDebounced(paramsFor(report, params))
  const missingOrg = Boolean(report?.org_required && !scoped.org)
  const previewQuery = useApiQuery<ReportPreview>(
    ['reports', 'preview', view, selected, scoped],
    `/reports/${selected}/preview`,
    { query: { view, limit: 100, ...scoped }, enabled: Boolean(report?.preview) && !missingOrg },
  )

  const groups = useMemo(() => {
    const reports = catalogue?.reports ?? []
    return [
      { label: 'Reports', items: reports.filter((r) => r.group === 'report') },
      { label: 'Record exports', items: reports.filter((r) => r.group === 'records') },
    ]
  }, [catalogue])

  async function exportCsv() {
    if (!report?.export_path) return
    setDownloading(true)
    try {
      const result = await downloadExport(`${API_BASE}${report.export_path}`, { view, ...scoped })
      toast({
        tone: 'success',
        message: `${result.rows.toLocaleString('en-GB')} rows exported. Export logged.`,
      })
      await queryClient.invalidateQueries({ queryKey: exportLogKey(view) })
    } catch (err) {
      toast({ tone: 'error', title: 'Export not created', message: errorMessage(err) })
    } finally {
      setDownloading(false)
    }
  }

  const committeeHref = report?.html_path
    ? buildUrl(`${API_BASE}${report.html_path}`, { view, ...scoped })
    : null
  function refreshExportLogLater() {
    for (const delay of REPORT_LOG_REFRESH_MS) {
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: exportLogKey(view) })
      }, delay)
    }
  }

  const header = (
    <PageHeader
      eyebrow="Reporting"
      title="A committee-ready view of the work."
      description="Choose the report and its scope. The preview and the download use the same server query, and every export is logged."
    />
  )
  if (catalogueQuery.isLoading) {
    return (
      <>
        {header}
        <LoadingRegion label="Loading reports…" />
      </>
    )
  }
  if (!catalogue) {
    return (
      <>
        {header}
        <Callout tone="danger" role="alert">
          {errorMessage(catalogueQuery.error)}
        </Callout>
      </>
    )
  }

  const columns: Column<Record<string, unknown>>[] = (previewQuery.data?.columns ?? []).map(
    (c) => ({
      id: c.key,
      header: <span className="whitespace-nowrap">{c.label}</span>,
      label: c.label,
      cell: (row) => {
        const text = cellText(row[c.key])
        return (
          <span className="block max-w-[22rem] truncate" title={text}>
            {text}
          </span>
        )
      },
    }),
  )

  return (
    <>
      {header}
      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <nav aria-label="Reports" className="hidden lg:block">
          {groups.map((g) => (
            <div key={g.label} className="mb-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-sage">
                {g.label}
              </p>
              <ul className="flex flex-col gap-1">
                {g.items.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      aria-current={r.id === selected ? 'page' : undefined}
                      onClick={() => setSelected(r.id)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left text-sm',
                        r.id === selected
                          ? 'bg-teal text-white'
                          : 'text-ink-soft hover:bg-wash hover:text-ink',
                      )}
                    >
                      {r.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="flex min-w-0 flex-col gap-6">
          <Select
            className="lg:hidden"
            label="Report"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            options={catalogue.reports.map((r) => ({
              value: r.id,
              label: r.group === 'records' ? `Records · ${r.title}` : r.title,
            }))}
          />
          {report ? (
            <Card
              title={report.title}
              eyebrow={report.group === 'records' ? 'Record export' : 'Report'}
              actions={
                <div className="flex flex-wrap gap-2">
                  {report.export_path ? (
                    <Button
                      icon={<Download />}
                      loading={downloading}
                      disabled={missingOrg}
                      onClick={exportCsv}
                    >
                      Export CSV
                    </Button>
                  ) : null}
                  {committeeHref ? (
                    missingOrg ? (
                      <Button icon={<Printer />} disabled>
                        Open committee report
                      </Button>
                    ) : (
                      <a
                        href={committeeHref}
                        target="_blank"
                        rel="noopener"
                        className={buttonClasses({})}
                        onClick={refreshExportLogLater}
                      >
                        <Printer aria-hidden="true" className="size-4" />
                        Open committee report
                        <ExternalLink aria-hidden="true" className="size-3.5" />
                      </a>
                    )
                  ) : null}
                </div>
              }
            >
              <p className="text-sm leading-relaxed text-ink-soft">{report.description}</p>
              {report.params.length ? (
                <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  {report.params.map((p) => (
                    <ParamField
                      key={p.key}
                      param={p}
                      value={params[p.key] ?? ''}
                      onChange={(v) => setParams((s) => ({ ...s, [p.key]: v }))}
                    />
                  ))}
                </div>
              ) : null}
              {missingOrg ? (
                <Callout tone="info" className="mt-4">
                  Choose a client to run this report.
                </Callout>
              ) : null}
            </Card>
          ) : null}

          {report?.preview ? (
            <Card
              title="Preview"
              actions={
                previewQuery.data ? (
                  <span className="text-sm text-sage">
                    {previewQuery.data.truncated
                      ? `Showing ${previewQuery.data.rows.length} of ${previewQuery.data.total_rows.toLocaleString('en-GB')} rows. The export includes all of them.`
                      : `${previewQuery.data.total_rows.toLocaleString('en-GB')} rows`}
                  </span>
                ) : null
              }
            >
              {previewQuery.isError ? (
                <Callout tone="danger" role="alert">
                  {errorMessage(previewQuery.error)}
                </Callout>
              ) : (
                <DataTable
                  caption={`${report.title} preview`}
                  columns={columns}
                  rows={(previewQuery.data?.rows ?? []) as Record<string, unknown>[]}
                  getRowId={(row) => String(Object.values(row)[0] ?? JSON.stringify(row))}
                  loading={previewQuery.isLoading && !missingOrg}
                  empty={
                    <p className="py-6 text-center text-sm text-sage">
                      {missingOrg ? 'Choose a client first.' : 'No rows match this scope.'}
                    </p>
                  }
                />
              )}
              {previewQuery.data &&
              previewQuery.data.total_rows > (catalogue.export_row_limit ?? 5000) ? (
                <Callout tone="warning" className="mt-4">
                  This scope has more than {catalogue.export_row_limit.toLocaleString('en-GB')}{' '}
                  rows. Narrow it before exporting; larger exports are not available yet.
                </Callout>
              ) : null}
            </Card>
          ) : report ? (
            <Card title="What the committee report contains">
              <ol className="list-decimal space-y-1.5 pl-5 text-sm text-ink-soft">
                {COMMITTEE_CONTENTS.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ol>
              <p className="mt-4 flex items-start gap-2 text-sm text-sage">
                <FileText aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                It opens as a print-ready page watermarked with your name, the time and “Fictional
                demo data”. Use Print, then “Save as PDF”, to keep a copy.
              </p>
            </Card>
          ) : null}

          <ExportHistory view={view} />
        </div>
      </div>
    </>
  )
}

function ParamField({
  param,
  value,
  onChange,
}: {
  param: ReportParamDescriptor
  value: string
  onChange: (value: string) => void
}) {
  if (param.type === 'date') {
    return (
      <DateInput label={param.label} value={value} onChange={(e) => onChange(e.target.value)} />
    )
  }
  if (param.type === 'select') {
    return (
      <Select
        label={param.label}
        required={param.required}
        placeholder={param.key === 'org' ? 'Choose a client' : 'All'}
        options={param.options ?? []}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    )
  }
  return (
    <Input
      label={param.label}
      hint={param.hint}
      value={value}
      maxLength={180}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}
